(() => {
  if (customElements.get('liene-inspiration')) return;
  let apiPromise;
  function youtubeAPI() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (!apiPromise) apiPromise = new Promise((resolve, reject) => {
      // Do not replace the theme's onYouTubeIframeAPIReady callback.
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script'); script.src = 'https://www.youtube.com/iframe_api'; document.head.append(script);
      }
      const started = Date.now();
      const timer = setInterval(() => {
        if (window.YT?.Player) { clearInterval(timer); resolve(window.YT); }
        else if (Date.now() - started > 15000) { clearInterval(timer); apiPromise = null; reject(new Error('YouTube unavailable')); }
      }, 100);
    });
    return apiPromise;
  }
  function source(card) {
    if (card.dataset.video) return { type: 'file', url: card.dataset.video };
    try {
      const url = new URL(card.dataset.social), host = url.hostname.replace(/^www\./, '');
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)) {
        const id = host === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v') || url.pathname.match(/^\/(?:shorts|embed|live)\/([\w-]+)/)?.[1];
        if (/^[\w-]{11}$/.test(id || '')) return { type: 'youtube', id };
      }
      if (host === 'instagram.com') {
        const match = url.pathname.match(/^\/(p|reel|reels|tv)\/([\w-]+)\/?$/);
        if (match) return { type: 'instagram', url: `https://www.instagram.com/${match[1] === 'reels' ? 'reel' : match[1]}/${match[2]}/` };
      }
    } catch (_) { /* Unconfigured cards remain disabled. */ }
    return null;
  }
  customElements.define('liene-inspiration', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.cards = [...this.querySelectorAll('[data-kind="video"]')];
      this.dialog = this.querySelector('dialog');
      this.cards.forEach(card => {
        card.source = source(card); card.wantsPlay = false; card.dataset.provider = card.source?.type || '';
        card.querySelectorAll('.lii__toggle,.lii__start').forEach(button => {
          button.disabled = !card.source;
          button.addEventListener('click', () => {
            if (button.classList.contains('lii__toggle') && card.dataset.state === 'playing') this.pause(card);
            else this.openVideo(card, button);
          }, opts);
        });
        if (card.source?.type === 'youtube' && !card.querySelector('.lii__cover img')) {
          const img = document.createElement('img'); img.src = `https://i.ytimg.com/vi/${card.source.id}/hqdefault.jpg`;
          img.alt = ''; img.loading = 'lazy'; img.width = 480; img.height = 360;
          card.querySelector('.lii__cover').replaceChildren(img);
        }
        const media = card.querySelector('.lii__media');
        media.addEventListener('pointerenter', event => {
          if (this.dialog.open || event.pointerType !== 'mouse' || this.dataset.hover !== 'true' || !matchMedia('(hover: hover) and (pointer: fine)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches || !card.source || card.source.type === 'instagram' || ['playing', 'ended'].includes(card.dataset.state)) return;
          card.hoveringPlay = true; this.play(card, true);
        }, opts);
        media.addEventListener('pointerleave', () => { if (card.hoveringPlay) this.pause(card); }, opts);

      });
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => { if (!entry.isIntersecting) this.pause(entry.target); }), { threshold: 0 });
      this.cards.forEach(card => this.observer.observe(card));
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.cards.forEach(card => this.pause(card)); }, opts);
      this.dialog.querySelector('.lii__close').addEventListener('click', () => this.dialog.close(), opts);
      this.dialog.addEventListener('close', () => { this.clearModal(); this.opener?.focus({ preventScroll: true }); }, opts);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      }, opts);
    }
    state(card, state) {
      card.dataset.state = state;
      const label = state === 'playing' ? 'Pause' : state === 'ended' ? 'Replay' : 'Play';
      card.querySelector('.lii__toggle').setAttribute('aria-label', `${label} ${card.dataset.title}`);
      card.querySelector('.lii__start').setAttribute('aria-label', `Open video: ${card.dataset.title}`);
      if (state === 'ended') { card.wantsPlay = false; card.hoveringPlay = false; }
    }
    error(card) {
      card.wantsPlay = false; this.state(card, 'idle');
      const status = card.querySelector('.lii__status'); status.hidden = false;
      status.textContent = 'Video could not play. Please try again.';
    }
    async play(card, muted) {
      if (!card.source) return;
      document.querySelectorAll('liene-inspiration').forEach(root => root.cards?.forEach(other => { if (other !== card) root.pause(other); }));
      const replay = card.dataset.state === 'ended';
      card.wantsPlay = true; card.muted = muted;
      card.querySelector('.lii__status').hidden = true;
      const generation = card.generation = (card.generation || 0) + 1;
      try {
        if (card.source.type === 'file') {
          if (!card.player) {
            const video = document.createElement('video'); video.src = card.source.url;
            video.playsInline = true; video.preload = 'metadata'; video.controls = false;
            video.addEventListener('playing', () => { if (!card.wantsPlay) video.pause(); else this.state(card, 'playing'); });
            video.addEventListener('pause', () => { if (!video.ended && card.dataset.state === 'playing') { card.wantsPlay = false; this.state(card, 'paused'); } });
            video.addEventListener('ended', () => this.state(card, 'ended'));
            video.addEventListener('error', () => this.error(card));
            card.player = video; card.querySelector('.lii__player').append(video);
          }
          card.player.muted = muted;
          if (replay) card.player.currentTime = 0;
          await card.player.play();
        } else if (card.source.type === 'youtube') {
          const YT = await youtubeAPI();
          if (!this.isConnected || !card.wantsPlay || generation !== card.generation) return;
          if (!card.player) {
            const mount = document.createElement('div'); card.querySelector('.lii__player').append(mount);
            card.player = new YT.Player(mount, {
              host: 'https://www.youtube-nocookie.com', videoId: card.source.id,
              playerVars: { playsinline: 1, controls: 0, rel: 0, origin: location.origin },
              events: {
                onReady: () => { card.ready = true; if (card.wantsPlay && this.isConnected) this.startYouTube(card, false); },
                onStateChange: event => {
                  if (event.data === 1) { if (!card.wantsPlay) card.player.pauseVideo(); else this.state(card, 'playing'); }
                  if (event.data === 2 && card.dataset.state === 'playing') { card.wantsPlay = false; this.state(card, 'paused'); }
                  if (event.data === 0) this.state(card, 'ended');
                },
                onError: () => this.error(card),
                onAutoplayBlocked: () => { card.wantsPlay = false; this.state(card, 'idle'); }
              }
            });
          } else if (card.ready) this.startYouTube(card, replay);
        }
      } catch (_) { if (generation === card.generation && card.wantsPlay) this.error(card); }
    }
    startYouTube(card, replay) {
      card.player[card.muted ? 'mute' : 'unMute']();
      if (replay) card.player.seekTo(0, true);
      card.player.playVideo();
    }
    pause(card) {
      card.wantsPlay = false; card.generation = (card.generation || 0) + 1;
      if (card.source?.type === 'file') card.player?.pause();
      else if (card.ready) card.player?.pauseVideo();
      if (card.dataset.state === 'playing') this.state(card, 'paused');
    }
    clearModal() {
      const video = this.querySelector('.lii__embed video');
      if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
      this.querySelector('.lii__embed').replaceChildren();
      if (this.savedOverflow !== undefined) {
        document.documentElement.style.overflow = this.savedOverflow;
        this.savedOverflow = undefined;
      }
    }
    openVideo(card, button) {
      if (!card.source) return;
      document.querySelectorAll('liene-inspiration').forEach(root => root.cards?.forEach(other => root.pause(other)));
      this.clearModal(); this.opener = button;
      const source = card.source, embed = this.querySelector('.lii__embed');
      this.dialog.dataset.provider = source.type;
      this.querySelector('.lii__dialog-title').textContent = card.dataset.title;
      const original = this.querySelector('.lii__original');
      original.hidden = source.type !== 'instagram';
      if (source.type === 'file') {
        const video = document.createElement('video'); video.src = source.url;
        video.controls = true; video.playsInline = true; video.autoplay = true;
        video.addEventListener('ended', () => this.state(card, 'ended'));
        video.addEventListener('error', () => this.error(card));
        embed.append(video);
      } else {
        const frame = document.createElement('iframe');
        frame.src = source.type === 'youtube'
          ? `https://www.youtube-nocookie.com/embed/${source.id}?autoplay=1&controls=1&playsinline=1&rel=0`
          : `${source.url}embed/`;
        frame.title = card.dataset.title || 'Video';
        frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen'; frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin'; embed.append(frame);
        if (source.type === 'instagram') original.href = source.url;
      }
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      embed.querySelector('video')?.play().catch(() => { /* Native play control remains available. */ });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect();
      this.cards?.forEach(card => { this.pause(card); if (card.source?.type === 'youtube') card.player?.destroy(); else if (card.player) { card.player.removeAttribute('src'); card.player.load(); } card.player = null; card.ready = false; card.querySelector('.lii__player').replaceChildren(); });
      this.clearModal();
    }
  });
})();
